import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { GridRowSelectionModel, GridSortDirection } from '@mui/x-data-grid-pro';

import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';

import useSearchProducts from '@/shared/hooks/useSearchProducts';
import ProductVariant from '@/shared/types/products/productVariant.type';
import RequestBody from '@/shared/components/dataDisplay/DataGridTable/types/requestBody.type';

const productRequiredColumns = [
  'product_id',
  'product_category',
  'product_name',
];

const productVariantsRequiredColumns = [
  'product_id',
  'variant_id',
  'product_category',
  'product_name',
  'variant_name',
];

type Args = {
  assignedVariants?: ProductVariant[];
  getSelectedRows?: (rowSelectionModel: GridRowSelectionModel) => void;
};

const useAssignProductsTable = ({
  assignedVariants,
  getSelectedRows,
}: Args) => {
  const { apiRef, model, requestBody } = useDataGridTable();

  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const rowSelectionModelRef = useRef<
    (typeof model)['rowSelectionModel'] | null
  >(null);

  const [level, setLevel] = useState<'products' | 'variants'>('products');

  const [table, setTable] = useState({
    data: [] as ProductVariant[],
    totalRows: 0,
  });

  const tableRequiredColumns = useMemo(() => {
    return level === 'products'
      ? productRequiredColumns
      : productVariantsRequiredColumns;
  }, [level]);

  const [customizedRequestBody, setCustomizedRequestBody] = useState<
    RequestBody | undefined
  >(undefined);

  const [showSelectedRows, setShowSelectedRows] = useState(false);
  const showSelectedRowsRef = useRef<typeof showSelectedRows | null>(null);

  const { productsResponse, tableColumnsList, loading } = useSearchProducts({
    searchObject: customizedRequestBody,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList || [],
      tableRequiredColumns,
    });

  useEffect(() => {
    const variants = productsResponse?.data.flatMap((item) => item.variants);

    const variantsByLevel = variants?.filter((item) =>
      level === 'products'
        ? item.variant_name === '_default'
        : item.variant_name !== '_default',
    );

    setTable((prev) => ({
      ...prev,
      data: variantsByLevel || [],
      totalRows: productsResponse?.total_records || 0,
    }));
  }, [productsResponse, level]);

  useEffect(() => {
    if (
      !isEqual(requestBody, requestBodyRef.current) ||
      !isEqual(model.rowSelectionModel, rowSelectionModelRef.current) ||
      !isEqual(showSelectedRows, showSelectedRowsRef.current)
    ) {
      requestBodyRef.current = requestBody;
      rowSelectionModelRef.current = model.rowSelectionModel;
      showSelectedRowsRef.current = showSelectedRows;

      let filter: RequestBody['search'] = requestBody.search;

      if (assignedVariants && assignedVariants.length > 0) {
        filter = [
          {
            variant_id: {
              cop: ['not', 'in'],
              value: assignedVariants.map((item) => item.variant_id),
            },
          },
          ...(filter.map((item, index) =>
            index === 0 ? { ...item, op: 'and' } : item,
          ) as RequestBody['search']),
        ];
      }

      if (model.rowSelectionModel.length > 0 && showSelectedRows) {
        filter = [
          {
            variant_id: {
              cop: ['in'],
              value: model.rowSelectionModel,
            },
          },
          ...(filter.map((item, index) =>
            index === 0 ? { ...item, op: 'and' } : item,
          ) as RequestBody['search']),
        ];
      }

      setCustomizedRequestBody({
        ...requestBody,
        sort_order: requestBody.sort_order as GridSortDirection,
        search: filter,
      });
    }
  }, [
    assignedVariants,
    model.rowSelectionModel,
    requestBody,
    showSelectedRows,
  ]);

  useEffect(() => {
    model.rowSelectionModel.length === 0 && setShowSelectedRows(false);

    getSelectedRows?.(model.rowSelectionModel);
  }, [model.rowSelectionModel, getSelectedRows]);

  return {
    loading,
    apiRef,
    model,
    table,
    level,
    columns,
    visibilityModel,
    showSelectedRows,
    setLevel,
    setVisibilityModel,
    setShowSelectedRows,
  };
};

export default useAssignProductsTable;
